/*
 * <accordion-slide> — smooth slide up / slide down for Dawn's product collapsible tabs.
 *
 * Wraps the stock `.product__accordion` <details>/<summary> markup. Dawn toggles
 * these instantly; this element intercepts the toggle and animates the row height
 * with the Web Animations API so it slides open and closed (no jQuery, no libs).
 *
 * Progressive enhancement: if the script fails or JS is off, the native <details>
 * still opens/closes. Honors prefers-reduced-motion (toggles instantly). Each
 * instance is independent, and it re-binds cleanly on Shopify section re-renders
 * because connectedCallback runs each time the element is inserted.
 */
if (!customElements.get('accordion-slide')) {
  customElements.define(
    'accordion-slide',
    class AccordionSlide extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('details');
        this.summary = this.details && this.details.querySelector('summary');
        this.content = this.querySelector('.accordion__content');

        // Bail (native toggle keeps working) if the structure is unexpected.
        if (!this.details || !this.summary || !this.content) return;

        // Guard against double-binding if the element is moved/reconnected.
        if (this._bound) return;
        this._bound = true;

        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.duration = 320;
        this.easing = 'cubic-bezier(0.33, 0.85, 0.4, 0.96)';

        this.summary.addEventListener('click', (event) => this.onClick(event));
      }

      get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      onClick(event) {
        event.preventDefault();

        // Reduced motion: skip the slide, just flip the native state.
        if (this.prefersReducedMotion) {
          this.details.open = !this.details.open;
          return;
        }

        this.details.style.overflow = 'hidden';

        if (this.isClosing || !this.details.open) {
          this.open();
        } else if (this.isExpanding || this.details.open) {
          this.shrink();
        }
      }

      // Slide up (collapse).
      shrink() {
        this.isClosing = true;

        const startHeight = `${this.details.offsetHeight}px`;
        const endHeight = `${this.summary.offsetHeight}px`;

        if (this.animation) this.animation.cancel();

        this.animation = this.details.animate(
          { height: [startHeight, endHeight] },
          { duration: this.duration, easing: this.easing }
        );

        this.animation.onfinish = () => this.onAnimationFinish(false);
        this.animation.oncancel = () => {
          this.isClosing = false;
        };
      }

      // Reveal content, then slide down (expand).
      open() {
        this.details.style.height = `${this.details.offsetHeight}px`;
        this.details.open = true;
        window.requestAnimationFrame(() => this.expand());
      }

      expand() {
        this.isExpanding = true;

        const startHeight = `${this.details.offsetHeight}px`;

        // Measure the full open height. Dawn's .accordion has a bottom border, so the
        // content's bottom margin is contained and offsetHeight includes it.
        this.details.style.height = 'auto';
        const fullHeight = this.details.offsetHeight;
        this.details.style.height = startHeight;
        const endHeight = `${fullHeight}px`;

        if (this.animation) this.animation.cancel();

        this.animation = this.details.animate(
          { height: [startHeight, endHeight] },
          { duration: this.duration, easing: this.easing }
        );

        this.animation.onfinish = () => this.onAnimationFinish(true);
        this.animation.oncancel = () => {
          this.isExpanding = false;
        };
      }

      onAnimationFinish(open) {
        this.details.open = open;
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.details.style.height = '';
        this.details.style.overflow = '';
      }
    }
  );
}
